/**
 * Per-plan-type config for the Payment Plan wizard. Encapsulates the fields
 * that vary between "Collection Plan", "Settlement Plan", "Settle for $0",
 * and "Resolution Plan":
 *   - which DDO slug names the plan type
 *   - whether discount fields are part of the body
 *   - whether a customer promotion code is accepted
 *   - whether discount values are validated against agent/manager max caps
 *   - whether the flow is the multi-step wizard or a single confirmation
 *   - which API endpoint the final submit hits — `submitEndpoint(loanId)`
 *     returns the path to append after `process.env.REACT_APP_API_DOMAIN`
 *
 * Mostly pure config; `submitEndpoint` is the one method-bearing field. It
 * lives here so the wizard hook stays plan-type-agnostic when it submits —
 * a regression on the resolution endpoint URL fails loud at the strategy
 * layer rather than getting buried in an inline `planSelection === 'X'`
 * branch in the hook. Helpers below combine strategies with runtime data
 * (form values, dropdown options) to produce request-body patches.
 *
 * Why a strategy module instead of inline `planSelection == "..."` ternaries:
 * the prior code branched on planSelection in 7+ places across the parent and
 * step components, with subtly different combinations of "include this field /
 * apply this label / show this section". A single source of truth keeps the
 * four flows from drifting apart, and makes adding a fifth flow a one-line
 * entry rather than a hunt.
 */

export const PAYMENT_PLAN_STRATEGY_KEYS = {
  COLLECTION: 'collection',
  SETTLEMENT: 'settlement',
  SETTLE_FOR_ZERO: 'settleforzero',
  RESOLUTION: 'resolution',
};

export const PAYMENT_PLAN_STRATEGIES = {
  [PAYMENT_PLAN_STRATEGY_KEYS.COLLECTION]: {
    key: PAYMENT_PLAN_STRATEGY_KEYS.COLLECTION,
    paymentPlanTypeSlug: 'payment_plan_type_collection',
    stepTitle: 'Collections',
    flow: 'wizard',
    includesDiscount: false,
    includesPromo: false,
    validatesDiscountAgainstMax: false,
    breakdownLabel: 'Payment plan Breakdown',
    buttonLabel: 'Create Collections Plan',
    buttonTestId: 'payment-plan-create-collection',
    tooltip: 'Intention is the customer will pay back the full amount owed. They have a max of two years per payment plan to do so, but they can have multiple payment plans',
    requiresManagerApproval: false,
    submitEndpoint: (loanId) => `/loan/${loanId}/payment_plan`,
  },
  [PAYMENT_PLAN_STRATEGY_KEYS.SETTLEMENT]: {
    key: PAYMENT_PLAN_STRATEGY_KEYS.SETTLEMENT,
    paymentPlanTypeSlug: 'payment_plan_type_settlement',
    stepTitle: 'Settlement',
    flow: 'wizard',
    includesDiscount: true,
    includesPromo: true,
    validatesDiscountAgainstMax: true,
    breakdownLabel: 'Settlement Breakdown',
    buttonLabel: 'Create Settlement Plan',
    buttonTestId: 'payment-plan-create-settlement',
    tooltip: 'Intention is to settle for less than the full amount owed. We give a discount with the expectation that is getting paid back in an accelerated timeframe',
    requiresManagerApproval: true,
    submitEndpoint: (loanId) => `/loan/${loanId}/payment_plan`,
  },
  [PAYMENT_PLAN_STRATEGY_KEYS.SETTLE_FOR_ZERO]: {
    key: PAYMENT_PLAN_STRATEGY_KEYS.SETTLE_FOR_ZERO,
    // Settle-for-zero re-uses the settlement DDO when fetching `/payment_plan/info`
    // (so existing agent/manager caps load) but never writes a payment_plan_type
    // on its final submit. The final submit hits a separate endpoint entirely.
    paymentPlanTypeSlug: 'payment_plan_type_settlement',
    stepTitle: 'Settle for $0',
    flow: 'single-step',
    includesDiscount: false,
    includesPromo: false,
    validatesDiscountAgainstMax: false,
    buttonLabel: 'Settle for $0',
    buttonTestId: 'payment-plan-settle-for-zero',
    tooltip: 'TODO: Helper Text',
    requiresManagerApproval: false,
    submitEndpoint: (loanId) => `/loan/${loanId}/settlement_payoff`,
  },
  [PAYMENT_PLAN_STRATEGY_KEYS.RESOLUTION]: {
    key: PAYMENT_PLAN_STRATEGY_KEYS.RESOLUTION,
    // Resolution covers two flows that share a strategy:
    //   1. QA-Resolution-then-flesh: Step 1 QA wrote a thin plan + paid
    //      forgiveness adjustment; the wizard adds the schedule via the
    //      flesh endpoint (`/payment_plan/payments` → storePaymentPlanPayments).
    //   2. Cancel-and-recreate: an active fleshed resolution plan was
    //      cancelled in Step 0; loan is back at resolution + no_res_plan
    //      with no thin plan. The wizard creates a new plan + schedule in
    //      one shot via the create endpoint (`/payment_plan` →
    //      storePaymentPlan), at the full restored outstanding (no fresh
    //      forgiveness — the prior paid forgiveness stays on the cancelled
    //      plan as the historical record).
    //
    // submitEndpoint is invoked at Step 3 with `{ hasExistingThinPlan }`;
    // wizard passes `!!responseData?.id` so the flesh path runs only when
    // a thin plan was actually loaded into responseData at modal open.
    //
    // No discount math at Step 2 of the wizard either way — for flesh,
    // outstanding equals the thin plan's owed_amount; for create, the
    // resolution amount is implied to be the full outstanding (zero
    // discount, allowed for resolution only — see PAYMENT_PLANS.md).
    paymentPlanTypeSlug: 'payment_plan_type_resolution',
    stepTitle: 'Resolution',
    flow: 'wizard',
    includesDiscount: false,
    includesPromo: false,
    validatesDiscountAgainstMax: false,
    breakdownLabel: 'Resolution Breakdown',
    buttonLabel: 'Create Resolution Plan',
    buttonTestId: 'payment-plan-create-resolution',
    tooltip: 'This option is only available if there has been a settlement with the tribe',
    requiresManagerApproval: false,
    submitEndpoint: (loanId, { hasExistingThinPlan = false } = {}) => {
      return hasExistingThinPlan
        ? `/loan/${loanId}/payment_plan/payments`
        : `/loan/${loanId}/payment_plan`
    },
  },
};

/**
 * Look up the strategy for a given planSelection value.
 * Returns null when planSelection is falsy (initial state, before the agent
 * has clicked a plan-type button) or unknown.
 */
export function getStrategy(planSelection) {
  if (!planSelection) return null;
  return PAYMENT_PLAN_STRATEGIES[planSelection] ?? null;
}

/**
 * Resolve the payment_plan_type DropdownOption id for the strategy. The
 * strategy stores the slug; the dropdownOptions list (from Redux) carries
 * the runtime ids.
 */
export function resolvePaymentPlanTypeId({ strategy, dropdownOptions }) {
  if (!strategy?.paymentPlanTypeSlug) return null;
  return dropdownOptions.find((o) => o?.code_key === strategy.paymentPlanTypeSlug)?.id ?? null;
}

/**
 * Returns the discount portion of a request body, or `{}` when the strategy
 * doesn't carry a discount. Mirrors the pre-strategy behaviour exactly:
 *   - settlement + percent → { discount_percent }
 *   - settlement + amount  → { discount_amount }
 *   - everything else      → {}
 *
 * Caller uses `Object.assign(body, buildDiscountBodyPatch({...}))` — keeping
 * the patch shape rather than mutating a body lets callers compose multiple
 * patches without each helper needing to know about the others.
 */
export function buildDiscountBodyPatch({
  strategy,
  settlementDiscountType,
  percentValue,
  amountValue,
}) {
  if (!strategy?.includesDiscount) return {};
  if (settlementDiscountType === 'percent') return { discount_percent: percentValue };
  if (settlementDiscountType === 'amount') return { discount_amount: amountValue };
  return {};
}

/**
 * Returns the customer-promotion-code portion of a request body, or `{}` when
 * the strategy doesn't accept a promo, or when no promo was selected.
 */
export function buildPromoBodyPatch({ strategy, customerPromotionCodeId }) {
  if (!strategy?.includesPromo) return {};
  if (!customerPromotionCodeId) return {};
  return { customer_promotion_code_id: customerPromotionCodeId };
}
